// src/services/file.service.js
import { getUserCredentials, ensureS3SafeFileName } from './api.service';
import axios from 'axios';
import { checkStorageLimits } from './api.service';
import { UPLOAD_ENDPOINTS, CONTENT_ENDPOINTS, DELETE_ENDPOINTS, DOWNLOAD_ENDPOINTS, STREAM_ENDPOINTS } from '../utils/constants';

import {
  getCurrentInheritance,
  uploadInheritance
} from './endowment.service';

const isMobile = () => {
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const userAgentMobile = mobileRegex.test(navigator.userAgent);
  const touchDevice = navigator.maxTouchPoints && navigator.maxTouchPoints > 2;
  const iPadOS = /MacIntel/.test(navigator.platform) && navigator.maxTouchPoints > 1;

  return userAgentMobile || touchDevice || iPadOS;
};

const CACHE_NAME = 'bucket-content-cache-v4';
const MULTIPART_THRESHOLD = 30 * 1024 * 1024;
const CHUNK_SIZE = isMobile() ? 2 * 1024 * 1024 : 5 * 1024 * 1024;

// Get token from session storage
export const getToken = () => {
  const credentials = getUserCredentials();
  return credentials ? credentials.token : sessionStorage.getItem('userToken');
};

/**
 * Check if files can be uploaded without exceeding storage limit
 * @param {File[]|FileList} files - Files to check
 * @returns {Promise<Object>} Check result with allowed boolean and details
 */
export const checkStorageLimit = async (files) => {
  if (!files || files.length === 0) {
    return { allowed: false, reason: 'No files selected' };
  }

  try {
    // Calculate total size of files to upload
    const fileArray = Array.from(files);
    const totalUploadSize = fileArray.reduce((total, file) => total + file.size, 0);


    // Fetch current storage limits
    const storageResponse = await checkStorageLimits();

    if (!storageResponse) {
      console.warn('Could not fetch storage limits, blocking upload for safety');
      return {
        allowed: false,
        reason: 'Unable to verify storage limits. Please try again.'
      };
    }

    // FIX: Use the correct property names from the API response (no .data property)
    const {
      currentSize: usedStorage = 0,        // ← Map currentSize to usedStorage
      maxSize: totalStorage = 0,           // ← Map maxSize to totalStorage
      remainingSpace = 0,                  // ← API already provides remainingSpace
      withinLimits = false                 // ← Additional check from API
    } = storageResponse;

    // You can use either calculation method:
    // Method 1: Calculate available storage
    const availableStorage = totalStorage - usedStorage;

    // Method 2: Use the pre-calculated remainingSpace from API (recommended)
    // const availableStorage = remainingSpace;

    // Enhanced check: Use both API flag and manual calculation
    if (!withinLimits || totalUploadSize > availableStorage) {
      const message = `Upload failed: Not enough storage space.\n\nUpload size: ${formatFileSize(totalUploadSize)}\nAvailable space: ${formatFileSize(availableStorage)}\n\nPlease delete some files or upgrade your storage plan.`;

      return {
        allowed: false,
        reason: message,
        uploadSize: totalUploadSize,
        availableStorage
      };
    }

    return {
      allowed: true,
      reason: 'Storage check passed',
      uploadSize: totalUploadSize,
      availableStorage
    };

  } catch (error) {
    console.error('Error checking storage limits:', error);
    return {
      allowed: false,
      reason: `Storage check failed: ${error.message}. Please try again.`
    };
  }
};

/**
 * Fetch bucket content with pagination
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Bucket content data
 */
export const getBucketContent = async (page = 1, limit = 10, token = getToken()) => {
  if (!token) {
    throw new Error('No authentication token found');
  }

  const userDetails = JSON.parse(sessionStorage.getItem("userDetails") || '{}');
  const userEmail = userDetails.Email || '';
  const url = CONTENT_ENDPOINTS.BUCKET_CONTENT_PAGE;
  const cacheKey = `${url}-files-${userEmail}-${page}-${limit}`;

  try {
    // Try to get from cache first
    const cachedData = await getCachedData(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    const data = {
      bucket: "files",
      page: page,
      limit: limit,
    };

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const processedResult = await processApiResult(result, page, limit);

    // Cache the processed result
    await setCachedData(cacheKey, processedResult);

    return processedResult;
  } catch (error) {
    console.error("Error fetching bucket content:", error);
    throw error;
  }
};

/**
 * Process API result to format data for display
 * @param {Object} result - API response data
 * @param {number} page - Current page number
 * @param {number} itemsPerPage - Items per page
 * @returns {Promise<Object>} Processed data
 */
export const processApiResult = async (result, page, itemsPerPage) => {
  const validItems = result.items.filter(
    (item) => item && (item.type === "file" || item.type === "folder")
  );

  const newItems = await Promise.all(
    validItems.map(async (item, index) => {
      if (item.type === "file") {
        let thumbnailData = null;
        if (item.Key.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/)) {
          // thumbnailData = await handleThumbnail(item.Key);
        }
        return {
          id: (page - 1) * itemsPerPage + index + 1,
          type: "file",
          name: item.Key,
          size: item.Size,
          lastEdited: item.LastModified,
          thumbnail: thumbnailData,
        };
      } else if (item.type === "folder") {
        return {
          id: (page - 1) * itemsPerPage + index + 1,
          type: "folder",
          name: item.Prefix,
          children: [],
        };
      }
      return null;
    })
  );

  return {
    items: newItems.filter(Boolean),
    totalItems: result.totalItems || 0,
    currentPage: result.currentPage || page,
    totalPages:
      result.totalPages || Math.ceil(result.totalItems / itemsPerPage),
  };
};

/**
 * Fetch folder contents
 * @param {string} folderPath - Path to the folder
 * @param {string} bucket - Bucket name
 * @param {boolean} bypassCache - Whether to bypass cache
 * @returns {Promise<Array>} Folder contents
 */
export const fetchFolderContents = async (folderPath, bucket = "files", bypassCache = false) => {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const userDetails = JSON.parse(sessionStorage.getItem("userDetails") || '{}');
  const userEmail = userDetails.Email || '';
  const formattedFolderPath = folderPath.endsWith('/') ? folderPath : `${folderPath}/`;
  const url = CONTENT_ENDPOINTS.FOLDER_CONTENT;
  const cacheKey = `folder-${bucket}-${folderPath}-${userEmail}`;

  try {
    if (!bypassCache) {
      const cachedData = await getCachedData(cacheKey);
      if (cachedData) {
        return cachedData;
      }
    }

    const data = {
      bucket: bucket,
      folderId: formattedFolderPath
    };

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const processedResult = processFolderContents(result.items, folderPath);

    // Cache the result
    await setCachedData(cacheKey, processedResult);

    return processedResult;
  } catch (error) {
    console.error("Error fetching folder contents:", error);
    throw error;
  }
};

/**
 * Process folder contents
 * @param {Array} items - Folder items
 * @param {string} currentFolderPath - Current folder path
 * @returns {Array} Processed folder items
 */
export const processFolderContents = (items, currentFolderPath) => {
  return items.map((item, index) => {
    let name, path;

    if (item.type === 'folder') {
      if (item.path) {
        const pathParts = item.path.split('/').filter(Boolean);
        name = pathParts[pathParts.length - 1];
        path = item.path;
      } else if (item.Prefix) {
        const prefixParts = item.Prefix.split('/').filter(Boolean);
        name = prefixParts[prefixParts.length - 1];
        path = item.Prefix;
      } else {
        name = item.name || 'Unnamed Folder';
        path = `${currentFolderPath}/${name}`.replace(/\/\//g, '/');
      }
    } else {
      name = item.name || (item.Key ? item.Key.split('/').pop() : 'Unnamed Item');
      path = item.path || `${currentFolderPath}/${name}`.replace(/\/\//g, '/');
    }

    path = path.replace(/\/\//g, '/');

    return {
      id: index + 1,
      type: item.type,
      name: name,
      path: path,
      size: item.size || item.Size || '',
      lastModified: item.lastModified || item.LastModified || '',
    };
  });
};

/**
 * Create a folder if it doesn't exist
 * @param {string} folderPath - Path of folder to create
 * @returns {Promise<boolean>} Success status
 */
export const createFolder = async (folderPath) => {
  if (!folderPath) {
    throw new Error('Folder path is required');
  }

  // Format the path correctly
  const formattedPath = folderPath.endsWith('/') ? folderPath : `${folderPath}/`;

  try {
    // First check if the folder exists
    const exists = await checkFolderExists(formattedPath);
    if (exists) {
      return true;
    }


    const token = getToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post(
      UPLOAD_ENDPOINTS.CREATE_FOLDER,
      {
        rootBucket: 'files',
        folderPath: formattedPath
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.status === 200;
  } catch (error) {
    console.error(`Error creating folder ${folderPath}:`, error);
    throw error;
  }
};

/**
 * Check if a folder exists
 * @param {string} folderPath - Path to check
 * @returns {Promise<boolean>} Whether folder exists
 */
export const checkFolderExists = async (folderPath) => {
  try {
    // Format the path correctly
    const formattedPath = folderPath.endsWith('/') ? folderPath : `${folderPath}/`;

    const token = getToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    // Try to get folder contents - if successful, the folder exists
    const response = await axios.post(
      CONTENT_ENDPOINTS.FOLDER_CONTENT,
      {
        bucket: 'files',
        folderId: formattedPath
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.status === 200;
  } catch (error) {
    // If we get a 404, the folder doesn't exist
    if (error.response && error.response.status === 404) {
      return false;
    }

    // For other errors, log and re-throw
    console.error(`Error checking if folder ${folderPath} exists:`, error);
    throw error;
  }
};

export const addFileToCurrentInheritance = async (filePath, token) => {
  try {
    if (!token) {
      const credentials = getUserCredentials();
      if (!credentials) {
        throw new Error('No authentication information found');
      }
      token = credentials.token;
    }

    const userDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
    const userEmail = userDetails.Email || '';

    if (!userEmail) {
      throw new Error('User email not found in session');
    }


    // Get the current (most recent) inheritance for this user
    const currentInheritance = await getCurrentInheritance(token, userEmail);

    if (!currentInheritance) {
      throw new Error('No active endowment found. Please create an endowment first in the Endowment Centre.');
    }

    // Check if the file is already in the inheritance
    if (currentInheritance.selectedItems.files && currentInheritance.selectedItems.files.includes(filePath)) {
      throw new Error('File is already added to this endowment');
    }

    // Add the file to the existing files array
    const updatedItems = {
      files: [...(currentInheritance.selectedItems.files || []), filePath],
      passwords: [...(currentInheritance.selectedItems.passwords || [])],
      banking: [...(currentInheritance.selectedItems.banking || [])],
      blockchain: [...(currentInheritance.selectedItems.blockchain || [])]
    };

    // Use uploadInheritance to update the inheritance with the new file
    await uploadInheritance({
      alias: currentInheritance.alias,
      account: currentInheritance.account,
      selectedItems: updatedItems,
      duration: currentInheritance.duration,
      Email: userEmail
    }, token);

    return true;
  } catch (error) {
    console.error('❌ Error adding file to current inheritance:', error);
    throw error;
  }
};

const updateInheritancesAfterFileDeletion = async (filePath, token) => {
  try {
    if (!token) {
      const credentials = getUserCredentials();
      if (!credentials) {
        throw new Error('No authentication information found');
      }
      token = credentials.token;
    }

    const userDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
    const userEmail = userDetails.Email || '';

    if (!userEmail) {
      return true;
    }

    const currentInheritance = await getCurrentInheritance(token, userEmail);

    if (!currentInheritance) {
      return true;
    }

    const fileIndex = currentInheritance.selectedItems.files?.indexOf(filePath) ?? -1;

    if (fileIndex === -1) {
      return true;
    }

    // Remove the file from the inheritance
    const updatedItems = {
      files: currentInheritance.selectedItems.files.filter(file => file !== filePath),
      passwords: [...(currentInheritance.selectedItems.passwords || [])],
      banking: [...(currentInheritance.selectedItems.banking || [])],
      blockchain: [...(currentInheritance.selectedItems.blockchain || [])]
    };

    await uploadInheritance({
      alias: currentInheritance.alias,
      account: currentInheritance.account,
      selectedItems: updatedItems,
      duration: currentInheritance.duration,
      Email: userEmail
    }, token);

    return true;
  } catch (error) {
    console.error('❌ Error updating inheritance after file deletion:', error);
    throw error;
  }
};

/**
 * Add all files from a folder to the current inheritance
 * @param {string} folderPath - Path to the folder
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Result with added files info
 */
export const addFolderToCurrentInheritance = async (folderPath, token) => {
  try {
    if (!token) {
      const credentials = getUserCredentials();
      if (!credentials) {
        throw new Error('No authentication information found');
      }
      token = credentials.token;
    }

    const userDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
    const userEmail = userDetails.Email || '';

    if (!userEmail) {
      throw new Error('User email not found in session');
    }


    // Get the current (most recent) inheritance for this user
    const currentInheritance = await getCurrentInheritance(token, userEmail);

    if (!currentInheritance) {
      throw new Error('No active endowment found. Please create an endowment first in the Endowment Centre.');
    }

    // Get all files in the folder recursively
    const folderFiles = await getAllFilesInFolder(folderPath, token);

    if (folderFiles.length === 0) {
      throw new Error('No files found in the specified folder');
    }


    // Check which files are not already in the inheritance
    const existingFiles = currentInheritance.selectedItems.files || [];
    const newFiles = folderFiles.filter(filePath => !existingFiles.includes(filePath));

    if (newFiles.length === 0) {
      throw new Error('All files in this folder are already added to the current endowment');
    }


    // Add the new files to the existing files array
    const updatedItems = {
      files: [...existingFiles, ...newFiles],
      passwords: [...(currentInheritance.selectedItems.passwords || [])],
      banking: [...(currentInheritance.selectedItems.banking || [])],
      blockchain: [...(currentInheritance.selectedItems.blockchain || [])]
    };

    // Use uploadInheritance to update the inheritance with the new files
    await uploadInheritance({
      alias: currentInheritance.alias,
      account: currentInheritance.account,
      selectedItems: updatedItems,
      duration: currentInheritance.duration,
      Email: userEmail
    }, token);


    return {
      success: true,
      addedFiles: newFiles,
      totalFiles: folderFiles.length,
      skippedFiles: folderFiles.length - newFiles.length,
      folderPath: folderPath
    };
  } catch (error) {
    console.error('❌ Error adding folder to current inheritance:', error);
    throw error;
  }
};

/**
 * Helper function to get all files in a folder recursively
 * @param {string} folderPath - Path to the folder
 * @param {string} token - Authentication token
 * @param {string} bucket - Bucket name
 * @returns {Promise<Array>} Array of file paths
 */
const getAllFilesInFolder = async (folderPath, token, bucket = 'files') => {
  const allFiles = [];

  try {

    // Use the existing fetchFolderContents function with cache bypass
    const folderContents = await fetchFolderContents(folderPath, bucket, true);

    for (const item of folderContents) {
      if (item.type === 'file') {
        // Add the file path (this will be in format "Folder/item1.txt")
        allFiles.push(item.path);
      } else if (item.type === 'folder') {
        // Recursively get files from subfolders
        const subFolderFiles = await getAllFilesInFolder(item.path, token, bucket);
        allFiles.push(...subFolderFiles);
      }
    }
  } catch (error) {
    console.error(`❌ Error fetching contents of folder ${folderPath}:`, error);
    throw error;
  }

  return allFiles;
};

/**
 * Update inheritances after folder deletion - removes all files from the deleted folder
 * @param {string} folderPath - Path to the deleted folder
 * @param {string} token - Authentication token
 * @returns {Promise<boolean>} Success status
 */
const updateInheritancesAfterFolderDeletion = async (folderPath, token) => {
  try {
    if (!token) {
      const credentials = getUserCredentials();
      if (!credentials) {
        throw new Error('No authentication information found');
      }
      token = credentials.token;
    }

    const userDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
    const userEmail = userDetails.Email || '';

    if (!userEmail) {
      return true;
    }

    const currentInheritance = await getCurrentInheritance(token, userEmail);

    if (!currentInheritance || !currentInheritance.selectedItems.files) {
      return true;
    }

    // Remove all files that start with the folder path
    const normalizedFolderPath = folderPath.endsWith('/') ? folderPath : `${folderPath}/`;
    const updatedFiles = currentInheritance.selectedItems.files.filter(filePath =>
      !filePath.startsWith(normalizedFolderPath) && filePath !== folderPath
    );

    // Only update if files were actually removed
    if (updatedFiles.length < currentInheritance.selectedItems.files.length) {
      const removedCount = currentInheritance.selectedItems.files.length - updatedFiles.length;

      const updatedItems = {
        files: updatedFiles,
        passwords: [...(currentInheritance.selectedItems.passwords || [])],
        banking: [...(currentInheritance.selectedItems.banking || [])],
        blockchain: [...(currentInheritance.selectedItems.blockchain || [])]
      };

      await uploadInheritance({
        alias: currentInheritance.alias,
        account: currentInheritance.account,
        selectedItems: updatedItems,
        duration: currentInheritance.duration,
        Email: userEmail
      }, token);

    }

    return true;
  } catch (error) {
    console.error('❌ Error updating inheritance after folder deletion:', error);
    throw error;
  }
};

/**
 * Delete multiple selected files in batches
 * @param {Array} selectedItems - Array of selected file/folder items
 * @param {string} bucket - Bucket name
 * @param {Function} progressCallback - Progress callback function
 * @param {AbortSignal} abortSignal - Abort signal for cancellation
 * @returns {Promise<Object>} Deletion results
 */
/**
 * Delete multiple selected files in batches
 * @param {Array} selectedItems - Array of selected file/folder items
 * @param {string} bucket - Bucket name
 * @param {Function} progressCallback - Progress callback function
 * @param {AbortSignal} abortSignal - Abort signal for cancellation
 * @returns {Promise<Object>} Deletion results
 */
export const deleteSelectedFiles = async (selectedItems, bucket = "files", progressCallback = () => { }, abortSignal = null) => {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  if (!selectedItems || selectedItems.length === 0) {
    throw new Error('No items selected for deletion');
  }

  const itemsToDelete = selectedItems.map(item => ({
    name: item.path || item.name,
    type: item.type,  // "file" or "folder"
    path: item.path || item.name
  }));


  const BATCH_SIZE = 50;
  const totalItems = itemsToDelete.length;
  const batches = [];

  // Split items into batches of 50
  for (let i = 0; i < itemsToDelete.length; i += BATCH_SIZE) {
    batches.push(itemsToDelete.slice(i, i + BATCH_SIZE));
  }


  // Initialize progress
  progressCallback(0, totalItems, `Starting deletion of ${totalItems} items in ${batches.length} batches...`);

  const results = {
    totalRequested: totalItems,
    totalDeleted: 0,
    totalFailed: 0,
    successCount: 0,
    failureCount: 0,
    batchResults: [],
    errors: []
  };

  let processedItems = 0;

  try {
    // Process each batch sequentially
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      // Check if request was aborted before processing each batch
      if (abortSignal?.aborted) {
        throw new DOMException('Request was cancelled', 'AbortError');
      }

      const batch = batches[batchIndex];
      const batchNumber = batchIndex + 1;


      // Update progress for current batch
      progressCallback(
        processedItems,
        totalItems,
        `Processing batch ${batchNumber}/${batches.length} (${batch.length} items)...`
      );

      const batchData = {
        bucket: bucket,
        items: batch  // Changed from 'fileNames' to 'items'
      };

      try {
        const response = await fetch(
          DELETE_ENDPOINTS.SELECTED_FILES,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            },
            body: JSON.stringify(batchData),
            credentials: "include",
            signal: abortSignal
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
          throw new Error(`HTTP error! Status: ${response.status}, Message: ${errorData.message || 'Unknown error'}`);
        }

        const batchResult = await response.json();

        // Track successful batch
        results.batchResults.push({
          batchNumber,
          itemsInBatch: batch.length,
          success: true,
          result: batchResult
        });

        results.totalDeleted += batch.length;
        results.successCount += batch.length;  // For backward compatibility
        processedItems += batch.length;


      } catch (batchError) {
        // Handle batch-specific errors
        console.error(`❌ Batch ${batchNumber} failed:`, batchError);

        results.batchResults.push({
          batchNumber,
          itemsInBatch: batch.length,
          success: false,
          error: batchError.message
        });

        results.totalFailed += batch.length;
        results.failureCount += batch.length;
        results.errors.push({
          batchNumber,
          error: batchError.message,
          items: batch  // Changed from 'files' to 'items'
        });

        processedItems += batch.length;

        // Continue with next batch unless it's an abort error
        if (batchError.name === 'AbortError') {
          throw batchError;
        }
      }

      // Update progress after each batch
      const successRate = ((results.totalDeleted / processedItems) * 100).toFixed(1);
      progressCallback(
        processedItems,
        totalItems,
        `Completed batch ${batchNumber}/${batches.length}. ${results.totalDeleted} deleted, ${results.totalFailed} failed (${successRate}% success)`
      );
    }

    // Final progress update
    if (results.totalFailed === 0) {
      progressCallback(totalItems, totalItems, `✅ All ${totalItems} items deleted successfully!`);
    } else {
      progressCallback(
        totalItems,
        totalItems,
        `⚠️ Completed: ${results.totalDeleted} deleted, ${results.totalFailed} failed`
      );
    }

    // Clear cache after all batches are processed
    await clearBucketContentCache();

    return results;

  } catch (error) {
    // Handle overall errors (like abort)
    if (error.name === 'AbortError') {
      progressCallback(processedItems, totalItems, 'Deletion cancelled');
      throw error;
    }

    console.error('❌ Error in bulk delete:', error);
    progressCallback(processedItems, totalItems, `Error: ${error.message}`);
    throw error;
  }
};

/**
 * Delete a file
 * @param {string} fileName - File name
 * @param {string} bucket - Bucket name
 * @returns {Promise<boolean>} Success status
 */
export const deleteFile = async (filePath, bucket = "files") => {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  // Log deletion attempt for debugging

  const data = {
    bucket: bucket,
    fileName: filePath
  };

  try {
    await updateInheritancesAfterFileDeletion(filePath, token);
  } catch (error) {
    console.error('Error updating inheritances:', error);
    // Continue with deletion even if inheritance update fails
  }

  try {
    const response = await fetch(
      DELETE_ENDPOINTS.FILE,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(data),
        credentials: "include",
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(`HTTP error! Status: ${response.status}, Message: ${errorData.message || 'Unknown error'}`);
    }

    // Clear cache after successful delete
    await clearBucketContentCache();

    return true;
  } catch (error) {
    console.error(`Error deleting file ${filePath}:`, error);
    throw error;
  }
};

/**
 * Delete a folder
 * @param {string} folderPath - Folder path
 * @returns {Promise<boolean>} Success status
 */
export const deleteFolder = async (folderPath) => {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const data = {
    bucket: "files",
    fileName: folderPath,
  };

  try {
    const response = await fetch(
      DELETE_ENDPOINTS.FOLDER,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
        credentials: "include",
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Clear cache after successful delete
    await clearBucketContentCache();

    return await response.json();
  } catch (error) {
    console.error("Error deleting folder:", error);
    throw error;
  }
};

/**
 * Download a folder as a zip
 * @param {string} folderPath - Folder path
 * @param {string} bucket - Bucket name
 * @param {Function} progressCallback - Progress callback
 * @param {AbortController} abortController - Abort controller
 * @returns {Promise<boolean>} Success status
 */
export const downloadFolder = async (folderPath, bucket = "files", progressCallback = () => { }, abortController = null) => {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const normalizedFolderPath = folderPath.endsWith('/') ? folderPath : `${folderPath}/`;
  const folderName = normalizedFolderPath.split('/').filter(Boolean).pop() || 'folder';

  try {

    progressCallback(5, `Preparing folder download...`);

    const response = await fetch(
      DOWNLOAD_ENDPOINTS.FOLDER,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bucket: bucket,
          folderPath: normalizedFolderPath
        }),
        credentials: "include",
        signal: abortController ? abortController.signal : undefined
      }
    );

    if (!response.ok) {
      throw new Error(`Server error: ${response.status} ${response.statusText}`);
    }

    progressCallback(10, `Creating zip file...`);

    const fileCount = response.headers.get("X-File-Count") || "0";
    const totalSize = parseInt(response.headers.get("X-Total-Size") || "0", 10);


    const reader = response.body.getReader();
    let receivedBytes = 0;
    let lastProgressUpdate = Date.now();

    const stream = new ReadableStream({
      async start(controller) {
        try {
          async function push() {
            try {
              const { done, value } = await reader.read();

              if (done) {
                controller.close();
                progressCallback(95, "Finishing download...");
                return;
              }

              controller.enqueue(value);
              receivedBytes += value.length;

              // Update progress more frequently
              const now = Date.now();
              if (now - lastProgressUpdate > 500 || receivedBytes === totalSize) { // Update every 500ms for folders
                const progress = totalSize > 0
                  ? Math.min(90, Math.round((receivedBytes / totalSize) * 80) + 10) // 10-90% range
                  : Math.min(90, 10 + Math.min(80, receivedBytes / 1024 / 1024)); // Fallback based on MB

                const progressMessage = totalSize > 0
                  ? `Downloading ${folderName}.zip: ${formatFileSize(receivedBytes)} of ${formatFileSize(totalSize)} (${fileCount} files)`
                  : `Downloading ${folderName}.zip: ${formatFileSize(receivedBytes)} (${fileCount} files)`;

                progressCallback(progress, progressMessage);
                lastProgressUpdate = now;
              }

              push();
            } catch (error) {
              console.error('❌ Error during folder read:', error);
              controller.error(error);
            }
          }

          push();
        } catch (err) {
          console.error('❌ Folder stream start error:', err);
          controller.error(err);
        }
      }
    });

    const newResponse = new Response(stream);
    const blob = await newResponse.blob();

    progressCallback(98, "Creating download...");

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = `${folderName}.zip`;
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);

    progressCallback(100, "Folder download complete!");
    return true;
  } catch (error) {
    if (error.name === 'AbortError') {
      progressCallback(100, "Download cancelled");
    } else {
      console.error("❌ Folder download error:", error);
      progressCallback(100, `Error: ${error.message}`);
    }
    throw error;
  }
};

/**
 * Download a file using presigned URL
 * @param {string} fileName - File name
 * @param {string} bucket - Bucket name
 * @param {Function} progressCallback - Progress callback
 * @param {AbortController} abortController - Abort controller
 * @returns {Promise<boolean>} Success status
 */
export const downloadFile = async (fileName, bucket = "files", fileSize = 0, progressCallback = () => { }, abortController = null) => {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  try {

    // Initial progress
    progressCallback(5, "Connecting to server...");

    // Use proxy method instead of presigned URL
    const response = await fetch(DOWNLOAD_ENDPOINTS.DOWNLOAD_PROXY, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        bucket,
        fileName
      }),
      credentials: "include",
      signal: abortController ? abortController.signal : undefined
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }

    progressCallback(10, "Download started...");

    // Get content info from headers
    const contentLength = response.headers.get("Content-Length");
    let downloadFileName = fileName.split("/").pop();
    const contentDispositionHeader = response.headers.get("Content-Disposition");

    if (contentDispositionHeader) {
      const filenameMatch = contentDispositionHeader.match(/filename="?(.+)"?/i);
      if (filenameMatch && filenameMatch.length > 1) {
        downloadFileName = filenameMatch[1].replace(/['"]/g, "");
      }
    }

    const totalBytes = fileSize || parseInt(contentLength, 10) || 0;;
    let receivedBytes = 0;
    let lastProgressUpdate = Date.now();


    const reader = response.body.getReader();
    const stream = new ReadableStream({
      async start(controller) {
        try {
          async function push() {
            try {
              const { done, value } = await reader.read();

              if (done) {
                controller.close();
                progressCallback(95, "Finishing download...");
                return;
              }

              controller.enqueue(value);
              receivedBytes += value.length;

              // Update progress more frequently and with better calculation
              const now = Date.now();
              if (now - lastProgressUpdate > 250 || receivedBytes === totalBytes) { // Update every 250ms
                const progress = totalBytes > 0
                  ? Math.min(95, Math.round((receivedBytes / totalBytes) * 85) + 10) // Perfect percentage!
                  : Math.min(90, 5 + (receivedBytes / (1024 * 1024)) * 2); // Fallback if no size

                const progressMessage = totalBytes > 0
                  ? `Downloading: ${formatFileSize(receivedBytes)} of ${formatFileSize(totalBytes)}`
                  : `Downloading: ${formatFileSize(receivedBytes)}`;

                progressCallback(progress, progressMessage);
                lastProgressUpdate = now;
              }

              push();
            } catch (error) {
              console.error('❌ Error during read:', error);
              controller.error(error);
            }
          }

          push();
        } catch (err) {
          console.error('❌ Stream start error:', err);
          controller.error(err);
        }
      }
    });

    const newResponse = new Response(stream);
    const blob = await newResponse.blob();

    progressCallback(98, "Creating download...");

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = downloadFileName;
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);

    progressCallback(100, "Download complete!");
    return true;

  } catch (error) {
    if (error.name === 'AbortError') {
      progressCallback(100, "Download cancelled");
    } else {
      console.error("❌ Download error:", error);
      progressCallback(100, `Error: ${error.message}`);
    }
    throw error;
  }
};

/**
 * View a file in the browser
 * @param {string} fileName - File name
 * @param {Function} onFileLoaded - Callback when file is loaded
 * @returns {Promise<Object>} File data
 */
export const getContentTypeFromFileName = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();

  const mimeTypes = {
    // Images
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'bmp': 'image/bmp',
    'svg': 'image/svg+xml',
    'webp': 'image/webp',

    // Documents
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',

    // Text files
    'txt': 'text/plain',
    'text': 'text/plain',
    'log': 'text/plain',
    'md': 'text/markdown',
    'markdown': 'text/markdown',
    'html': 'text/html',
    'htm': 'text/html',
    'css': 'text/css',
    'js': 'text/javascript',
    'jsx': 'text/javascript',
    'ts': 'text/typescript',
    'tsx': 'text/typescript',
    'json': 'application/json',
    'xml': 'text/xml',
    'yaml': 'text/yaml',
    'yml': 'text/yaml',

    // Code files
    'py': 'text/x-python',
    'java': 'text/x-java',
    'cpp': 'text/x-c++src',
    'c': 'text/x-csrc',
    'h': 'text/x-chdr',
    'cs': 'text/x-csharp',
    'php': 'text/x-php',
    'rb': 'text/x-ruby',
    'go': 'text/x-go',
    'rs': 'text/x-rust',
    'swift': 'text/x-swift',
    'kt': 'text/x-kotlin',
    'sql': 'text/x-sql',
    'sh': 'text/x-shellscript',
    'bash': 'text/x-shellscript',

    // Config files
    'ini': 'text/plain',
    'conf': 'text/plain',
    'config': 'text/plain',
    'env': 'text/plain',
    'properties': 'text/plain',
    'toml': 'text/plain',
    'cfg': 'text/plain',

    // Audio
    'mp3': 'audio/mpeg',
    'wav': 'audio/wav',
    'ogg': 'audio/ogg',
    'flac': 'audio/flac',

    // Video
    'mp4': 'video/mp4',
    'avi': 'video/avi',
    'mov': 'video/quicktime',
    'wmv': 'video/x-ms-wmv',
    'mkv': 'video/x-matroska',
    'webm': 'video/webm',

    // Archives
    'zip': 'application/zip',
    'rar': 'application/x-rar-compressed',
    '7z': 'application/x-7z-compressed'
  };

  return mimeTypes[extension] || 'application/octet-stream';
};

/**
 * View a file with proper text content support
 * @param {Object} fileItem - File item with name and path
 * @returns {Promise<Object>} File data with presigned URL and text content
 */
export const viewFile = async (fileItem) => {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const filePath = fileItem.path || fileItem.name;
  const contentType = getContentTypeFromFileName(fileItem.name);
  const isTextFile = isTextBasedFile(fileItem.name);

  try {

    // For text files, use streaming method to get content reliably
    if (isTextFile && fileItem.size < 5 * 1024 * 1024) { // Text files under 5MB
      return await viewTextFileStreaming(fileItem, filePath, contentType);
    }

    // For non-text files (images, PDFs, videos), use presigned URL
    return await viewMediaFilePresigned(fileItem, filePath, contentType);

  } catch (error) {
    console.error('❌ Error viewing file:', error);
    throw new Error(`Failed to view file: ${error.message}`);
  }
};

/**
 * Check if a file is text-based (for editing/viewing as text)
 */
const isTextBasedFile = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();

  const textExtensions = [
    // Plain text
    'txt', 'text', 'log', 'md', 'markdown', 'readme',

    // Code files
    'js', 'jsx', 'ts', 'tsx', 'json', 'xml', 'yaml', 'yml',
    'html', 'htm', 'css', 'scss', 'sass', 'less',
    'php', 'py', 'java', 'c', 'cpp', 'h', 'hpp',
    'cs', 'vb', 'rb', 'go', 'rs', 'swift', 'kt',
    'sql', 'sh', 'bash', 'ps1', 'bat', 'cmd',
    'r', 'r', 'scala', 'clj', 'hs', 'ml', 'fs',
    'vue', 'svelte', 'dart', 'gradle', 'cmake',

    // Config files
    'ini', 'conf', 'config', 'env', 'properties',
    'toml', 'cfg', 'settings', 'plist',

    // Documentation
    'tex', 'latex', 'rtf', 'csv', 'tsv'
  ];

  return textExtensions.includes(extension);
};

/**
 * View text file using streaming method (for reliable text content)
 */
const viewTextFileStreaming = async (fileItem, filePath, contentType) => {
  const token = getToken();

  const response = await fetch(STREAM_ENDPOINTS.FILE, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      bucket: 'files',
      fileName: filePath,
    }),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const responseContentType = response.headers.get('Content-Type');
  const blob = await response.blob();

  // Get text content
  let textContent = '';
  try {
    textContent = await blob.text();
  } catch (err) {
    console.error('❌ Failed to read text content:', err);
    throw new Error('Failed to read text content from file');
  }

  // Create blob URL for display (in case viewer needs it)
  const url = URL.createObjectURL(blob);

  return {
    fileName: fileItem.name,
    name: fileItem.name,
    originalName: fileItem.name,
    path: filePath,
    contentType: responseContentType || contentType,
    url, // Blob URL for backup display
    blob,
    textContent, // ✅ Text content guaranteed for text files
    size: fileItem.size,
    lastModified: fileItem.lastModified || fileItem.lastEdited,
    isTextFile: true // ✅ Flag to indicate this is a text file
  };
};

/**
 * View media file using presigned URL (for images, PDFs, videos)
 */
const viewMediaFilePresigned = async (fileItem, filePath, contentType) => {
  const token = getToken();

  // Generate presigned URL for viewing
  const response = await fetch(STREAM_ENDPOINTS.GENERATE_VIEW_URL, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      bucket: 'files',
      fileName: filePath,
      expiresIn: 3600 // 1 hour
    }),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const urlData = await response.json();

  if (!urlData.success || !urlData.viewUrl) {
    throw new Error('Failed to generate view URL');
  }


  return {
    fileName: fileItem.name,
    name: fileItem.name,
    originalName: fileItem.name,
    path: filePath,
    contentType: contentType,
    url: urlData.viewUrl, // ✅ Presigned URL for direct display
    viewUrl: urlData.viewUrl,
    blob: null, // No blob for media files
    textContent: null, // No text content for media files
    size: fileItem.size,
    lastModified: fileItem.lastModified || fileItem.lastEdited,
    expiresAt: urlData.expiresAt,
    expiresIn: urlData.expiresIn,
    isTextFile: false // ✅ Flag to indicate this is not a text file
  };
};

/**
 * Upload files or folders with intelligent endpoint selection
 * @param {File[]|FileList} files - Files to upload
 * @param {string} targetPath - Upload destination path
 * @param {Function} progressCallback - Progress callback
 * @param {AbortController} controller - Abort controller
 * @returns {Promise<Object>} Result with success info
 */
export const uploadFiles = async (files, targetPath = '', progressCallback = () => { }, controller = null) => {
  if (!files || files.length === 0) {
    return { success: false, message: 'No files selected for upload' };
  }

  // Convert FileList to Array
  const fileArray = Array.from(files);

  try {
    // STEP 1: Check storage limits FIRST
    progressCallback(0, 'Checking storage limits...');

    const storageCheck = await checkStorageLimit(files);

    if (!storageCheck.allowed) {
      // Show alert to user and stop upload
      alert(`❌ Upload Blocked\n\n${storageCheck.reason}`);
      progressCallback(100, 'Upload blocked due to storage limit');
      return {
        success: false,
        message: storageCheck.reason,
        blocked: true
      };
    }


    // STEP 2: Continue with existing upload logic
    progressCallback(2, `Analyzing ${fileArray.length} files for upload...`);

    // Log diagnostic info

    // Separate files by size and check for folder uploads
    const largeFiles = [];
    const smallFiles = [];
    let isFolderUpload = false;

    fileArray.forEach((file, index) => {

      // Check if this is part of a folder upload
      if (file.webkitRelativePath) {
        isFolderUpload = true;
      }

      if (file.size > MULTIPART_THRESHOLD) {
        largeFiles.push(file);
      } else {
        smallFiles.push(file);
      }
    });


    // Handle folder uploads differently
    if (isFolderUpload) {
      return await uploadFolderWithRename(fileArray, targetPath, progressCallback, controller);
    }

    const results = [];
    const totalSize = fileArray.reduce((sum, file) => sum + file.size, 0);
    let uploadedSize = 0;
    let completedFiles = 0;

    // Upload large files sequentially (they handle their own concurrency internally)
    for (const file of largeFiles) {
      progressCallback(
        Math.round((uploadedSize / totalSize) * 100),
        `Uploading large file: ${file.name} (${completedFiles + 1}/${fileArray.length})`
      );

      const fileProgressCallback = (fileProgress, message) => {
        const fileContribution = (file.size / totalSize) * (fileProgress / 100);
        const currentProgress = (uploadedSize / totalSize) + fileContribution;
        progressCallback(
          Math.round(currentProgress * 100),
          message || `Uploading ${file.name}...`
        );
      };

      try {
        const result = await uploadLargeFileWithRename(file, targetPath, fileProgressCallback, controller);

        results.push({ file: file.name, success: true, result });
        completedFiles++;
        uploadedSize += file.size;
      } catch (error) {
        console.error(`Error uploading large file ${file.name}:`, error);
        results.push({ file: file.name, success: false, error: error.message });

        // For large files, we might want to stop on error since they're uploaded individually
        if (controller?.signal?.aborted) {
          break;
        }
      }
    }

    // Upload small files with controlled concurrency (max 3 at a time)
    if (smallFiles.length > 0) {

      const SMALL_FILE_CONCURRENCY = 3;
      const smallFileResults = [];

      // Process small files in batches
      for (let i = 0; i < smallFiles.length; i += SMALL_FILE_CONCURRENCY) {
        const batch = smallFiles.slice(i, i + SMALL_FILE_CONCURRENCY);

        // Update progress for this batch
        progressCallback(
          Math.round((uploadedSize / totalSize) * 100),
          `Uploading batch of small files (${completedFiles + 1}-${completedFiles + batch.length}/${fileArray.length})`
        );

        // Create upload promises for this batch
        const batchPromises = batch.map(async (file, batchIndex) => {
          const globalIndex = i + batchIndex;

          const fileProgressCallback = (fileProgress, message) => {
            // For small files in parallel, we calculate approximate progress
            const fileContribution = (file.size / totalSize) * (fileProgress / 100);
            const baseProgress = uploadedSize / totalSize;
            const estimatedProgress = baseProgress + fileContribution;

            progressCallback(
              Math.round(estimatedProgress * 100),
              `File ${globalIndex + 1}/${smallFiles.length}: ${message || file.name}`
            );
          };

          try {
            const result = await uploadSingleFileWithRename(file, targetPath, fileProgressCallback, controller);

            return { file: file.name, success: true, result, size: file.size };
          } catch (error) {
            console.error(`Error uploading small file ${file.name}:`, error);
            return { file: file.name, success: false, error: error.message, size: file.size };
          }
        });

        // Wait for this batch to complete
        const batchResults = await Promise.all(batchPromises);
        smallFileResults.push(...batchResults);

        // Update uploaded size for completed files in this batch
        batchResults.forEach(result => {
          if (result.success) {
            uploadedSize += result.size;
            completedFiles++;
          }
        });

        // Check if upload was cancelled
        if (controller?.signal?.aborted) {
          break;
        }
      }

      results.push(...smallFileResults);
    }

    // Calculate final stats
    const successCount = results.filter(r => r.success).length;
    const renamedFiles = results.filter(r => r.success && r.result?.wasRenamed);

    let completionMessage;
    if (successCount === fileArray.length) {
      completionMessage = `All ${fileArray.length} files uploaded successfully`;
      if (renamedFiles.length > 0) {
        completionMessage += ` (${renamedFiles.length} files renamed to avoid conflicts)`;
      }
      progressCallback(100, completionMessage);
      return { success: true, message: completionMessage, results };
    } else {
      completionMessage = `Uploaded ${successCount}/${fileArray.length} files`;
      if (renamedFiles.length > 0) {
        completionMessage += ` (${renamedFiles.length} files renamed)`;
      }
      progressCallback(100, completionMessage);
      return {
        success: successCount > 0,
        partial: successCount > 0 && successCount < fileArray.length,
        message: completionMessage,
        results
      };
    }
  } catch (error) {
    console.error('Error in intelligent upload process:', error);
    progressCallback(100, `Upload failed: ${error.message}`);
    return { success: false, error: error.message };
  }
};

/**
 * Check if a file exists in the specified path
 * @param {string} fileName - File name to check
 * @param {string} folderPath - Folder path to check in
 * @param {string} bucket - Bucket name
 * @returns {Promise<boolean>} Whether file exists
 */
export const checkFileExists = async (fileName, folderPath = '', bucket = 'files') => {
  try {
    const token = getToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    // Construct the full file path
    const fullPath = folderPath ? `${folderPath}/${fileName}` : fileName;


    // Use folder content endpoint to check if file exists
    // This is more reliable than the metadata endpoint
    const folderToCheck = folderPath || ''; // Empty string for root

    let response;
    if (folderToCheck) {
      // Check within a specific folder
      response = await fetch(CONTENT_ENDPOINTS.FOLDER_CONTENT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bucket: bucket,
          folderId: folderToCheck.endsWith('/') ? folderToCheck : `${folderToCheck}/`
        }),
        credentials: "include",
      });
    } else {
      // Check in root directory
      response = await fetch(CONTENT_ENDPOINTS.BUCKET_CONTENT_PAGE, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bucket: bucket,
          page: 1,
          limit: 1000 // Large limit to get all files
        }),
        credentials: "include",
      });
    }

    if (!response.ok) {
      return false;
    }

    const result = await response.json();
    const items = result.items || [];

    // Check if any item matches our filename
    const fileExists = items.some(item => {
      if (item.type === 'file') {
        // For files, check the name or Key property
        const itemName = item.name || (item.Key ? item.Key.split('/').pop() : '');
        return itemName === fileName;
      }
      return false;
    });

    return fileExists;

  } catch (error) {
    // If there's an error, assume file doesn't exist to avoid blocking uploads
    console.warn(`Error checking if file exists: ${error.message}, assuming file doesn't exist`);
    return false;
  }
};

/**
 * Generate a unique file name by checking for conflicts and adding numbers
 * @param {string} originalFileName - Original file name
 * @param {string} folderPath - Folder path where file will be uploaded
 * @param {string} bucket - Bucket name
 * @returns {Promise<string>} Unique file name
 */
export const generateUniqueFileName = async (originalFileName, folderPath = '', bucket = 'files') => {

  const safeFileName = ensureS3SafeFileName(originalFileName);
  // First check if the original name is available
  const exists = await checkFileExists(safeFileName, folderPath, bucket);
  if (!exists) {
    return safeFileName;
  }

  // Extract file name and extension
  const lastDotIndex = safeFileName.lastIndexOf('.');
  let baseName, extension;

  if (lastDotIndex === -1) {
    // No extension
    baseName = safeFileName;
    extension = '';
  } else {
    baseName = safeFileName.substring(0, lastDotIndex);
    extension = safeFileName.substring(lastDotIndex);
  }

  // Try numbered variations
  let counter = 1;
  let newFileName;

  do {
    newFileName = `${baseName}(${counter})${extension}`;

    const newExists = await checkFileExists(newFileName, folderPath, bucket);
    if (!newExists) {
      return newFileName;
    }

    counter++;

    // Safety check to prevent infinite loops
    if (counter > 1000) {
      throw new Error('Could not generate unique filename after 1000 attempts');
    }
  } while (true);
};

/**
 * Upload a single file with automatic name conflict resolution
 * @param {File} file - File to upload
 * @param {string} folderPath - Folder path (full path including nested structure)
 * @param {Function} progressCallback - Progress callback
 * @param {AbortController} controller - Abort controller
 * @returns {Promise<Object>} Result with final filename
 */
export const uploadSingleFileWithRename = async (file, folderPath, progressCallback = () => { }, controller = null, preCalculatedFileName = null) => {
  try {
    // Extract just the filename from the file (without path)
    const originalFileName = file.webkitRelativePath ?
      file.webkitRelativePath.split('/').pop() :
      file.name;

    progressCallback(0, `Checking for name conflicts...`);

    // Generate unique filename
    const uniqueFileName = preCalculatedFileName || await generateUniqueFileName(originalFileName, folderPath);

    if (uniqueFileName !== originalFileName) {
      progressCallback(5, `Renamed to ${uniqueFileName} to avoid conflict`);
    }

    progressCallback(10, `Preparing to upload ${uniqueFileName}...`);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', uniqueFileName);
    formData.append('fileType', file.type || 'application/octet-stream');
    formData.append('bucket', 'files');
    formData.append('filePath', uniqueFileName);

    if (folderPath && folderPath.trim() !== '') {
      formData.append('folderParent', folderPath);
    }

    const token = getToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await axios.post(UPLOAD_ENDPOINTS.SINGLE_FILE, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        const adjustedProgress = 10 + (percentCompleted * 0.9);
        progressCallback(adjustedProgress, `Uploading ${uniqueFileName}...`);
      },
      signal: controller?.signal
    });

    progressCallback(100, `Uploaded ${uniqueFileName} successfully`);

    // Clear cache after successful upload
    await clearBucketContentCache();

    return {
      success: true,
      data: response.data,
      originalFileName,
      finalFileName: uniqueFileName,
      wasRenamed: uniqueFileName !== originalFileName
    };

  } catch (error) {
    if (error.name === 'AbortError' || controller?.signal?.aborted) {
      progressCallback(100, 'Upload cancelled');

      // Create proper AbortError for upload queue recognition
      const cancelError = new Error('Upload cancelled');
      cancelError.name = 'AbortError';
      throw cancelError; // ✅ Re-throw for proper handling
    } else {
      console.error(`Error uploading file:`, error);
      progressCallback(100, `Upload failed: ${error.message}`);
      throw error;
    }
  }
};



// Function to get appropriate concurrent uploads based on device
const getConcurrentUploads = () => {
  return isMobile() ? 3 : 6;
};

const debugFileChunking = (file, chunkSize) => {
  const totalParts = Math.ceil(file.size / chunkSize);

  // Analyze each part size
  const parts = [];
  for (let i = 1; i <= totalParts; i++) {
    const start = (i - 1) * chunkSize;
    const end = Math.min(i * chunkSize, file.size);
    const partSize = end - start;

    parts.push({
      partNumber: i,
      start,
      end,
      size: partSize,
      sizeMB: (partSize / 1024 / 1024).toFixed(2)
    });
  }

  const showParts = parts.length > 6 ? [...parts.slice(0, 3), null, ...parts.slice(-3)] : parts;
  showParts.forEach(part => {
    if (part === null) {
    } else {
      const isLast = part.partNumber === totalParts;
    }
  });

  const totalCalculatedSize = parts.reduce((sum, part) => sum + part.size, 0);

  if (totalCalculatedSize !== file.size) {
    console.error(`🚨 SIZE MISMATCH: Difference = ${totalCalculatedSize - file.size} bytes`);
  }

  return parts;
};

const createPartUploadTracker = (expectedParts, originalFileSize) => {
  const uploadedParts = new Map(); // partNumber -> { ETag, size, timestamp }
  const duplicateAttempts = new Map(); // partNumber -> count

  return {
    recordUpload: (partNumber, etag, actualSize) => {
      // Check for duplicates
      if (uploadedParts.has(partNumber)) {
        const existing = uploadedParts.get(partNumber);
        console.warn(`⚠️ DUPLICATE PART DETECTED: Part ${partNumber} already exists`);
        console.warn(`   Existing: ETag=${existing.ETag.slice(0, 8)}..., Size=${existing.size}`);
        console.warn(`   New: ETag=${etag.slice(0, 8)}..., Size=${actualSize}`);

        const duplicateCount = (duplicateAttempts.get(partNumber) || 0) + 1;
        duplicateAttempts.set(partNumber, duplicateCount);

        return false; // Don't overwrite
      }

      uploadedParts.set(partNumber, {
        ETag: etag,
        size: actualSize,
        timestamp: Date.now(),
        partNumber
      });

      return true;
    },

    debugReport: () => {
      const parts = Array.from(uploadedParts.values()).sort((a, b) => a.partNumber - b.partNumber);
      const totalUploadedSize = parts.reduce((sum, part) => sum + part.size, 0);
      const duplicates = Array.from(duplicateAttempts.entries());

      if (duplicates.length > 0) {
        console.warn(`⚠️ DUPLICATE ATTEMPTS: ${duplicates.map(([p, c]) => `Part ${p}: ${c}x`).join(', ')}`);
      }

      if (totalUploadedSize !== originalFileSize) {
        console.error(`🚨 SIZE MISMATCH: +${((totalUploadedSize - originalFileSize) / 1024 / 1024).toFixed(2)} MB`);
      }

      return {
        parts,
        totalSize: totalUploadedSize,
        hasCorrectSize: totalUploadedSize === originalFileSize,
        duplicates
      };
    },

    getPartsForCompletion: () => {
      return Array.from(uploadedParts.values())
        .sort((a, b) => a.partNumber - b.partNumber)
        .map(part => ({
          PartNumber: part.partNumber,
          ETag: part.ETag
        }));
    }
  };
};

/**
 * Upload a large file using multipart upload with streaming concurrency and automatic name conflict resolution
 * @param {File} file - File to upload
 * @param {string} folderPath - Folder path
 * @param {Function} progressCallback - Progress callback
 * @param {AbortController} controller - Abort controller
 * @returns {Promise<Object>} Result with final filename
 */
export const uploadLargeFileWithRename = async (file, folderPath, progressCallback = () => { }, controller = null, onMultipartInit = null, preCalculatedFileName = null) => {
  let uploadId = null;
  let key = null;
  const token = getToken();
  const bucket = 'files';
  const CONCURRENT_UPLOADS = getConcurrentUploads(); // Dynamic based on device
  const MAX_RETRIES = 3;
  const SAFETY_LIMIT_MULTIPLIER = 3; // Prevent infinite loops

  // Create internal cancellation tracking
  let isCancelled = false;
  const cleanup = () => {
    isCancelled = true;
    if (uploadId) {
      localStorage.removeItem(`upload_${uploadId}`);
    }
  };

  // Safe progress callback with error protection
  const safeProgressCallback = (progress, message) => {
    if (!isCancelled && !controller?.signal?.aborted) {
      try {
        progressCallback(Math.min(100, Math.max(0, progress)), message);
      } catch (error) {
        console.warn('Progress callback failed:', error);
      }
    }
  };

  try {
    // Step 0: Generate unique filename
    safeProgressCallback(0, `Checking for name conflicts...`);
    const originalFileName = file.name;
    const uniqueFileName = preCalculatedFileName || await generateUniqueFileName(originalFileName, folderPath);

    if (uniqueFileName !== originalFileName) {
      safeProgressCallback(2, `Renamed to ${uniqueFileName} to avoid conflict`);
    }

    // Set the initial key value
    key = folderPath ? `${folderPath}/${uniqueFileName}` : uniqueFileName;

    // Step 1: Initiate multipart upload with unique filename
    safeProgressCallback(5, `Initiating multipart upload for ${uniqueFileName}...`);

    const initiateResponse = await axios.post(UPLOAD_ENDPOINTS.INITIATE_MULTIPART, {
      fileName: uniqueFileName,
      bucket: bucket,
      filePath: uniqueFileName,
      fileType: file.type,
      folderParent: folderPath || null
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      signal: controller?.signal,
      timeout: 30000 // 30 second timeout
    });

    uploadId = initiateResponse.data.uploadId;
    key = initiateResponse.data.key || key;

    if (onMultipartInit) {
      onMultipartInit({
        uploadId: uploadId,
        key: key,
        bucket: bucket
      });
    }

    if (!uploadId) {
      throw new Error('Failed to initiate multipart upload');
    }

    // Store upload metadata with TTL
    const uploadMetadata = {
      uploadId,
      key,
      bucket,
      fileName: uniqueFileName,
      timestamp: Date.now(),
      expiresAt: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
      type: 'multipart',
      totalParts: Math.ceil(file.size / CHUNK_SIZE),
      chunkSize: CHUNK_SIZE,
      completedParts: []
    };

    try {
      localStorage.setItem(`upload_${uploadId}`, JSON.stringify(uploadMetadata));
    } catch (storageError) {
      console.warn('Failed to store upload metadata:', storageError);
      // Continue without localStorage - not critical
    }

    // Step 2: Upload parts with improved streaming concurrency
    const totalParts = Math.ceil(file.size / CHUNK_SIZE);
    const uploadedParts = new Array(totalParts);
    const partFailures = new Map(); // Track failures per part
    const partRetries = new Map(); // Track retry counts
    let nextPartNumber = 1;
    let completedParts = 0;
    let activeCount = 0;
    let hasError = false;

    // 🔍 DEBUG: Analyze file chunking before upload
    debugFileChunking(file, CHUNK_SIZE);

    // Create size tracking for duplicate detection
    const partTracker = createPartUploadTracker(totalParts, file.size);

    // Improved progress calculation
    const updateOverallProgress = () => {
      if (isCancelled || controller?.signal?.aborted) return;

      try {
        // Use the completedParts counter from the new concurrency logic
        const overallProgress = (completedParts / totalParts) * 89;
        const percentage = Math.min(99, Math.max(5, 5 + overallProgress));

        safeProgressCallback(
          percentage,
          `Uploading ${uniqueFileName}:`
        );
      } catch (error) {
        console.warn('Progress update failed:', error);
      }
    };

    // Helper function to upload a single part with retry logic
    const uploadPart = async (partNumber) => {
      // Early cancellation check
      if (isCancelled || controller?.signal?.aborted) {
        throw new Error('Upload cancelled');
      }

      const retryCount = partRetries.get(partNumber) || 0;
      if (retryCount >= MAX_RETRIES) {
        throw new Error(`Part ${partNumber} failed after ${MAX_RETRIES} retries`);
      }

      let chunk = null;

      try {
        const start = (partNumber - 1) * CHUNK_SIZE;
        const end = Math.min(partNumber * CHUNK_SIZE, file.size);
        chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append('file', chunk, uniqueFileName);
        formData.append('partNumber', partNumber.toString());
        formData.append('uploadId', uploadId);
        formData.append('bucket', bucket);
        formData.append('key', key);

        // 🔧 FIX: Save chunk size BEFORE clearing memory
        const actualPartSize = chunk.size;

        const partResponse = await axios.post(UPLOAD_ENDPOINTS.UPLOAD_PART, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          signal: controller?.signal,
          timeout: 120000 // 60 second timeout per part
        });

        // Clear memory references immediately
        chunk = null;

        // ✅ CRITICAL: Store result immediately and atomically
        if (!isCancelled && !controller?.signal?.aborted && partResponse.data.ETag) {
          const partResult = {
            PartNumber: partNumber,
            ETag: partResponse.data.ETag
          };
          // Store in array with validation
          uploadedParts[partNumber - 1] = partResult;


          const recorded = partTracker.recordUpload(partNumber, partResponse.data.ETag, actualPartSize);
          if (!recorded) {
            console.error(`❌ DUPLICATE DETECTED: Part ${partNumber} was already uploaded!`);
          }

          updateOverallProgress();

          // Update localStorage with completed part
          try {
            const storedMetadata = JSON.parse(localStorage.getItem(`upload_${uploadId}`) || '{}');
            if (storedMetadata.completedParts && !storedMetadata.completedParts.includes(partNumber)) {
              storedMetadata.completedParts.push(partNumber);
              localStorage.setItem(`upload_${uploadId}`, JSON.stringify(storedMetadata));
            }
          } catch (e) {
            // Ignore localStorage errors
          }

          // Clear any previous failure record
          partFailures.delete(partNumber);
          partRetries.delete(partNumber);

          return { partNumber, ETag: partResponse.data.ETag, success: true };
        } else {
          console.warn(`⚠️ Part ${partNumber} upload cancelled or no ETag received`);
          return { partNumber, success: false, error: 'Upload cancelled or no ETag' };
        }

      } catch (error) {
        // Clear memory references on error
        chunk = null;

        // Handle different types of errors
        if (error.name === 'AbortError' || error.message === 'Upload cancelled') {
          throw error; // Re-throw cancellation errors
        } else {
          // Track failure and retry count
          partFailures.set(partNumber, {
            error: error.message,
            timestamp: Date.now(),
            retryCount: retryCount + 1
          });
          partRetries.set(partNumber, retryCount + 1);

          console.error(`❌ Part ${partNumber} failed (attempt ${retryCount + 1}/${MAX_RETRIES}):`, error.message);

          // Decide whether to retry or fail
          if (retryCount + 1 < MAX_RETRIES) {
            await new Promise(resolve => setTimeout(resolve, 1000)); // Brief delay before retry
            return await uploadPart(partNumber); // Recursive retry
          } else {
            return { partNumber, success: false, error: error.message };
          }
        }
      }
    };



    const allPartsPromise = new Promise((resolve, reject) => {

      const startNextPart = () => {
        if (nextPartNumber > totalParts || isCancelled || controller?.signal?.aborted || hasError) {
          return; // No more parts to start
        }

        const partNumber = nextPartNumber++;
        activeCount++;


        uploadPart(partNumber)
          .then(result => {
            // Success - store result
            if (result.success) {
              uploadedParts[partNumber - 1] = {
                PartNumber: result.partNumber,
                ETag: result.ETag
              };
              completedParts++;
              updateOverallProgress();
            } else {
              console.warn(`⚠️ Part ${partNumber} failed but not throwing: ${result.error}`);
            }
            return result;
          })
          .catch(error => {
            // Handle upload error
            if (error.name === 'AbortError' || error.message === 'Upload cancelled') {
            } else {
              console.error(`Part ${partNumber} error:`, error.message);
              hasError = true;
              reject(error);
              return;
            }
          })
          .finally(() => {
            activeCount--;

            // 🔥 KEY FIX: Immediately start next part when this one finishes
            if (nextPartNumber <= totalParts && !hasError && !isCancelled && !controller?.signal?.aborted) {
              startNextPart(); // Start replacement part immediately
            }

            // Check if we're completely done
            if (completedParts >= totalParts) {
              resolve(); // All parts completed successfully
            } else if (activeCount === 0 && nextPartNumber > totalParts) {
              // No active parts and no more to start
              if (completedParts < totalParts) {
                console.warn(`⚠️ Upload finished but incomplete: ${completedParts}/${totalParts} parts`);
                resolve(); // Resolve anyway, let validation handle it
              }
            }
          });
      };

      // Start initial batch of concurrent uploads
      const initialConcurrency = Math.min(CONCURRENT_UPLOADS, totalParts);
      for (let i = 0; i < initialConcurrency; i++) {
        startNextPart();
      }

      // Handle the case where totalParts is 0
      if (totalParts === 0) {
        resolve();
      }
    });

    // Wait for all parts to complete
    await allPartsPromise;

    // Log first few and last few parts for debugging
    const debugParts = [];
    for (let i = 0; i < Math.min(5, totalParts); i++) {
      debugParts.push(`Part ${i + 1}: ${uploadedParts[i] ? `✅ ETag=${uploadedParts[i].ETag?.slice(0, 8)}...` : '❌ Missing'}`);
    }
    if (totalParts > 10) {
      debugParts.push('...');
      for (let i = Math.max(5, totalParts - 5); i < totalParts; i++) {
        debugParts.push(`Part ${i + 1}: ${uploadedParts[i] ? `✅ ETag=${uploadedParts[i].ETag?.slice(0, 8)}...` : '❌ Missing'}`);
      }
    }

    // Check for the specific missing part around 113
    if (totalParts >= 113) {
      for (let i = Math.max(0, 110); i < Math.min(totalParts, 116); i++) {
        const part = uploadedParts[i];
      }
    }

    // Final cancellation check
    if (isCancelled || controller?.signal?.aborted) {
      cleanup();
      throw new Error('Upload cancelled');
    }

    // Step 3: Validate parts and complete multipart upload with simpler validation
    safeProgressCallback(95, `Validating and completing upload for ${uniqueFileName}...`);

    // Simple validation approach - check what we have and proceed if reasonable
    const validParts = uploadedParts.filter(part => part && part.ETag && part.PartNumber);
    const missingCount = totalParts - validParts.length;
    const missingPercentage = (missingCount / totalParts) * 100;

    if (missingCount === 0) {
    } else if (missingPercentage <= 5) { // Allow up to 5% missing parts as acceptable
      console.warn(`⚠️ ${missingCount} parts missing (${missingPercentage.toFixed(1)}%) - proceeding anyway`);
      console.warn(`💡 This might be due to race conditions in tracking. Upload should still work.`);
    } else {
      console.error(`🚨 Too many parts missing (${missingPercentage.toFixed(1)}%) - aborting`);
      throw new Error(`Upload failed: ${missingCount}/${totalParts} parts missing. This indicates a serious upload issue. Please try again.`);
    }

    // Sort parts by part number for completion
    const sortedParts = validParts.sort((a, b) => a.PartNumber - b.PartNumber);

    // Complete with valid parts
    const completeResponse = await axios.post(UPLOAD_ENDPOINTS.COMPLETE_MULTIPART, {
      key: key,
      uploadId: uploadId,
      parts: sortedParts, // Use the valid parts we found
      bucket: bucket,
      fileType: file.type
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      signal: controller?.signal,
      timeout: 120000
    });

    safeProgressCallback(100, `Successfully uploaded ${uniqueFileName}`);

    // Clear cache and localStorage after successful upload
    await clearBucketContentCache();
    localStorage.removeItem(`upload_${uploadId}`);

    return {
      ...completeResponse.data,
      originalFileName,
      finalFileName: uniqueFileName,
      wasRenamed: uniqueFileName !== originalFileName,
      uploadStats: {
        totalParts,
        successfulParts: validParts.length,  // Use the simple valid parts count
        failedParts: partFailures.size,
        retries: Array.from(partRetries.values()).reduce((sum, count) => sum + count, 0),
        missingFromTracking: totalParts - validParts.length  // New stat to track race condition impact
      }
    };

  } catch (error) {
    cleanup();

    let isUserCancellation = false;
    let errorType = 'unknown';

    // Determine error type
    if (error.name === 'AbortError' || error.message === 'Upload cancelled' || controller?.signal?.aborted) {
      isUserCancellation = true;
      errorType = 'user_cancelled';
    } else if (error.message?.includes('network') || error.message?.includes('Network Error') || error.code === 'NETWORK_ERROR') {
      errorType = 'network_error';
    } else if (error.message?.includes('All parts failed') || error.message?.includes('Too many part failures')) {
      errorType = 'parts_failed';
    } else if (error.message?.includes('timed out') || error.message?.includes('timeout')) {
      errorType = 'timeout_error';
    } else if (error.message?.includes('server') || error.response?.status >= 500) {
      errorType = 'server_error';
    } else if (error.response?.status >= 400 && error.response?.status < 500) {
      errorType = 'client_error';
    } else {
      errorType = 'unknown_error';
    }

    // ✅ CLEANUP S3 FOR ALL FAILURES (not just user cancellation)
    if (uploadId && key) {
      try {
        await abortMultipartUpload(uploadId, key, bucket, token);
      } catch (abortError) {
        console.error(`❌ S3 cleanup failed for ${uploadId}:`, abortError.message);
        // Don't throw cleanup errors - the original error is more important
      }
    }

    // Handle different error types appropriately
    if (isUserCancellation) {
      safeProgressCallback(0, 'Upload cancelled');
      const cancelError = new Error('Upload cancelled');
      cancelError.name = 'AbortError';
      throw cancelError;
    } else {
      // All other errors
      const errorMessage = `Upload failed: ${error.message}`;
      safeProgressCallback(0, errorMessage);
      console.error('Upload error details:', {
        type: errorType,
        message: error.message,
        status: error.response?.status,
        uploadId: uploadId,
        cleanedUp: !!(uploadId && key)
      });
      throw error;
    }
  }
};

/**
 * Updated uploadFile function that automatically handles renaming
 * @param {File} file - File to upload
 * @param {string} folderPath - Folder path
 * @param {Function} progressCallback - Progress callback
 * @param {AbortController} controller - Abort controller
 * @returns {Promise<Object>} Result with renaming info
 */
export const uploadFileWithAutoRename = async (file, folderPath, progressCallback = () => { }, controller = null, onMultipartInit = null) => {
  // Use multipart upload for large files
  if (file.size > MULTIPART_THRESHOLD) {
    return uploadLargeFileWithRename(file, folderPath, progressCallback, controller, onMultipartInit);
  } else {
    return uploadSingleFileWithRename(file, folderPath, progressCallback, controller);
  }
};


/**
 * Upload a file 
 * @param {File} file - File to upload
 * @param {string} folderPath - Folder path
 * @param {Function} progressCallback - Progress callback
 * @param {AbortController} abortController - Abort controller
 * @returns {Promise<Object>} Result
 */
export const uploadFile = async (file, folderPath, progressCallback = () => { }, controller = null) => {
  return uploadFileWithAutoRename(file, folderPath, progressCallback, controller);
};

/**
 * Abort a multipart upload
 * @param {string} uploadId - Upload ID
 * @param {string} key - Object key
 * @param {string} bucket - Bucket name
 * @param {string} token - Auth token
 * @returns {Promise<void>}
 */
export const abortMultipartUpload = async (uploadId, key, bucket, token) => {
  if (!uploadId || !key || !bucket) {
    console.warn('⚠️ Missing parameters for S3 abort - skipping cleanup');
    return false;
  }

  try {

    const response = await axios.post(UPLOAD_ENDPOINTS.ABORT_MULTIPART, {
      uploadId,
      key,
      bucket
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      timeout: 15000 // 15 second timeout for cleanup
    });

    if (response.status === 200) {
      return true;
    } else {
      console.warn(`⚠️ Unexpected response status ${response.status} for S3 abort`);
      return false;
    }
  } catch (error) {
    console.error(`❌ Failed to abort S3 multipart upload ${uploadId}:`, error.message);
    // Log but don't throw - cleanup errors shouldn't crash the app
    return false;
  }
};

/**
 * Check if a folder exists in the specified path
 * @param {string} folderName - Folder name to check
 * @param {string} parentPath - Parent path to check in
 * @param {string} bucket - Bucket name
 * @returns {Promise<boolean>} Whether folder exists
 */
export const checkFolderNameExists = async (folderName, parentPath = '', bucket = 'files') => {
  try {
    const token = getToken();
    if (!token) {
      throw new Error('No authentication token found');
    }


    let response;
    if (parentPath) {
      // Check within a specific parent folder
      response = await fetch(CONTENT_ENDPOINTS.FOLDER_CONTENT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bucket: bucket,
          folderId: parentPath.endsWith('/') ? parentPath : `${parentPath}/`
        }),
        credentials: "include",
      });
    } else {
      // Check in root directory
      response = await fetch(CONTENT_ENDPOINTS.BUCKET_CONTENT_PAGE, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bucket: bucket,
          page: 1,
          limit: 1000
        }),
        credentials: "include",
      });
    }

    if (!response.ok) {
      return false;
    }

    const result = await response.json();
    const items = result.items || [];

    // Check if any item is a folder with matching name
    const folderExists = items.some(item => {
      if (item.type === 'folder') {
        const itemName = item.name || (item.Prefix ? item.Prefix.split('/').filter(Boolean).pop() : '');
        return itemName === folderName;
      }
      return false;
    });

    return folderExists;

  } catch (error) {
    console.warn(`Error checking if folder exists: ${error.message}, assuming folder doesn't exist`);
    return false;
  }
};

/**
 * Generate a unique folder name by checking for conflicts and adding numbers
 * @param {string} originalFolderName - Original folder name
 * @param {string} parentPath - Parent path where folder will be created
 * @param {string} bucket - Bucket name
 * @returns {Promise<string>} Unique folder name
 */
export const generateUniqueFolderName = async (originalFolderName, parentPath = '', bucket = 'files') => {
  // First check if the original name is available
  const exists = await checkFolderNameExists(originalFolderName, parentPath, bucket);
  if (!exists) {
    return originalFolderName;
  }

  // Try numbered variations
  let counter = 1;
  let newFolderName;

  do {
    newFolderName = `${originalFolderName}(${counter})`;

    const newExists = await checkFolderNameExists(newFolderName, parentPath, bucket);
    if (!newExists) {
      return newFolderName;
    }

    counter++;

    // Safety check to prevent infinite loops
    if (counter > 1000) {
      throw new Error('Could not generate unique folder name after 1000 attempts');
    }
  } while (true);
};

/**
 * Upload a folder structure with nested folders and files (with automatic renaming)
 * @param {File[]} files - Array of files from a folder
 * @param {string} rootPath - Base path to upload to
 * @param {Function} progressCallback - Progress callback
 * @param {AbortController} controller - Abort controller
 * @returns {Promise<Object>} Result with rename info
 */
export const uploadFolderWithRename = async (files, rootPath = '', progressCallback = () => { }, controller = null, onMultipartInit = null) => {
  if (!files || files.length === 0) {
    return { success: false, message: 'No files provided' };
  }

  const token = getToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  try {
    // STEP 1: Check storage limits FIRST (if not already checked)
    progressCallback(0, 'Checking storage limits...');

    const storageCheck = await checkStorageLimit(files);

    if (!storageCheck.allowed) {
      // Show alert to user and stop upload
      alert(`❌ Folder Upload Blocked\n\n${storageCheck.reason}`);
      progressCallback(100, 'Folder upload blocked due to storage limit');
      return {
        success: false,
        message: storageCheck.reason,
        blocked: true
      };
    }

    // STEP 2: Continue with existing folder upload logic
    progressCallback(1, `Analyzing folder structure...`);

    // Find files with webkitRelativePath to confirm this is a folder upload
    const filesWithPaths = Array.from(files).filter(file => file.webkitRelativePath);
    if (filesWithPaths.length === 0) {
      console.warn("No files have webkitRelativePath, cannot process as folder upload");
      throw new Error("Invalid folder structure. Please try uploading again.");
    }

    // Get the root folder name and check for conflicts
    const firstPath = filesWithPaths[0].webkitRelativePath;
    const originalRootFolderName = firstPath.split('/')[0];

    progressCallback(2, `Checking for folder name conflicts...`);

    // Generate unique root folder name
    const uniqueRootFolderName = await generateUniqueFolderName(originalRootFolderName, rootPath);
    const wasRootFolderRenamed = uniqueRootFolderName !== originalRootFolderName;

    if (wasRootFolderRenamed) {
      progressCallback(3, `Root folder renamed to ${uniqueRootFolderName} to avoid conflict`);
    }

    // Process files and build full paths
    const filesToUpload = [];
    const renamedFiles = [];
    const folderPaths = new Set(); // Track all unique folder paths

    progressCallback(5, `Processing file paths...`);

    for (const file of filesWithPaths) {
      const relativePath = file.webkitRelativePath;
      const pathParts = relativePath.split('/');
      const originalFileName = pathParts.pop(); // Remove filename from path parts

      // Replace the first part (root folder) with the unique name
      if (pathParts.length > 0) {
        pathParts[0] = uniqueRootFolderName;
      }

      // Build the full file path including all folder structure
      const folderStructure = pathParts.join('/');
      const fullFilePath = rootPath
        ? `${rootPath}/${folderStructure}/${originalFileName}`
        : `${folderStructure}/${originalFileName}`;

      // Check for file name conflicts at this specific path
      const fileDirectory = rootPath
        ? `${rootPath}/${folderStructure}`
        : folderStructure;

      const uniqueFileName = await generateUniqueFileName(originalFileName, fileDirectory);
      const wasFileRenamed = uniqueFileName !== originalFileName;

      if (wasFileRenamed) {
      }

      const folderPath = rootPath
        ? `${rootPath}/${folderStructure}`
        : folderStructure;

      const finalFullPath = folderPath
        ? `${folderPath}/${uniqueFileName}`
        : uniqueFileName;

      // **NEW: Track all folder paths that need to be created**
      if (folderStructure) {
        const fullFolderPath = rootPath
          ? `${rootPath}/${folderStructure}`
          : folderStructure;

        // Add all parent folder paths
        const parts = fullFolderPath.split('/');
        for (let i = 1; i <= parts.length; i++) {
          const partialPath = parts.slice(0, i).join('/');
          if (partialPath) {
            folderPaths.add(partialPath);
          }
        }
      }

      filesToUpload.push({
        file,
        originalPath: relativePath,
        finalFullPath,
        folderPath,
        originalFileName,
        uniqueFileName,
        wasRenamed: wasFileRenamed,
        folderStructure
      });

      // Track all renames
      if (wasRootFolderRenamed || wasFileRenamed) {
        renamedFiles.push({
          originalPath: relativePath,
          finalPath: finalFullPath,
          folderRenamed: wasRootFolderRenamed,
          fileRenamed: wasFileRenamed,
          originalFileName,
          finalFileName: uniqueFileName
        });
      }
    }

    // **NEW: STEP 3: Create all necessary folders first**
    const uniqueFolderPaths = Array.from(folderPaths).sort(); // Sort to ensure parent folders are created first

    progressCallback(8, `Creating folder structure (${uniqueFolderPaths.length} folders)...`);

    for (const folderPath of uniqueFolderPaths) {
      try {
        await createFolderInPath(folderPath);
      } catch (error) {
        console.warn(`Failed to create folder ${folderPath}:`, error.message);
        // Continue anyway - the upload might still work if the server auto-creates folders
      }
    }

    // Calculate sizes for progress tracking
    const totalSize = filesToUpload.reduce((sum, item) => sum + item.file.size, 0);
    let uploadedSize = 0;
    let uploadedFiles = 0;
    const uploadResults = [];

    // Upload files with full paths (folders should now exist)
    progressCallback(10, `Uploading ${filesToUpload.length} files...`);

    for (const fileInfo of filesToUpload) {
      const { file, finalFullPath, originalFileName, uniqueFileName, wasRenamed } = fileInfo;

      try {
        // Check for abort
        if (controller?.signal?.aborted) {
          throw new Error('Upload cancelled');
        }

        // Progress tracking function for this file
        const fileProgress = (progress, message) => {
          const fileContribution = (file.size / totalSize) * (progress / 100);
          const totalProgress = 10 + ((uploadedSize / totalSize) * 90) + (fileContribution * 90);
          progressCallback(
            Math.min(99, Math.round(totalProgress)),
            `${message || `Uploading ${uniqueFileName}`} (${uploadedFiles + 1}/${filesToUpload.length})`
          );
        };

        let result;
        // Choose upload method based on file size and upload with correct folder path
        if (file.size > MULTIPART_THRESHOLD) {
          result = await uploadLargeFileWithRename(file, fileInfo.folderPath, fileProgress, controller, onMultipartInit, fileInfo.uniqueFileName);
        } else {
          result = await uploadSingleFileWithRename(file, fileInfo.folderPath, fileProgress, controller, fileInfo.uniqueFileName);
        }

        // Update progress tracking
        uploadedFiles++;
        uploadedSize += file.size;
        uploadResults.push({
          originalPath: fileInfo.originalPath,
          finalPath: finalFullPath,
          file: originalFileName,
          finalName: uniqueFileName,
          success: true,
          wasRenamed: wasRenamed
        });

      } catch (err) {
        console.error(`Error uploading file ${originalFileName}:`, err);
        uploadResults.push({
          file: originalFileName,
          finalPath: finalFullPath,
          success: false,
          error: err.message
        });
        // Continue with other files
      }
    }

    // Final progress update
    const successCount = uploadResults.filter(r => r.success).length;
    let completionMessage = `Folder upload complete: ${successCount}/${filesToUpload.length} files uploaded`;

    if (wasRootFolderRenamed) {
      completionMessage += ` (folder renamed to "${uniqueRootFolderName}")`;
    }

    if (renamedFiles.length > 0) {
      const fileRenames = renamedFiles.filter(rf => rf.fileRenamed).length;
      if (fileRenames > 0) {
        completionMessage += ` (${fileRenames} files also renamed to avoid conflicts)`;
      }
    }

    progressCallback(100, completionMessage);

    // Clear cache to ensure latest data is displayed
    await clearBucketContentCache();

    return {
      success: successCount > 0,
      complete: successCount === filesToUpload.length,
      message: completionMessage,
      results: uploadResults,
      renamedFiles: renamedFiles,
      originalFolderName: originalRootFolderName,
      finalFolderName: uniqueRootFolderName,
      wasFolderRenamed: wasRootFolderRenamed,
      foldersCreated: uniqueFolderPaths.length
    };
  } catch (error) {
    console.error('Error uploading folder:', error);
    progressCallback(100, `Upload failed: ${error.message}`);
    return { success: false, error: error.message };
  }
};

/**
 * Upload a folder structure with nested folders and files
 * @param {File[]} files - Array of files from a folder
 * @param {string} rootPath - Base path to upload to
 * @param {Function} progressCallback - Progress callback
 * @param {AbortController} controller - Abort controller
 * @returns {Promise<Object>} Result
 */
export const uploadFolder = async (files, rootPath = '', progressCallback = () => { }, controller = null) => {
  return uploadFolderWithRename(files, rootPath, progressCallback, controller);
};

/**
 * Create a folder in the given path using the existing endpoint
 * @param {string} folderPath - Path of folder to create
 * @returns {Promise<boolean>} Success status
 */
export const createFolderInPath = async (folderPath) => {
  if (!folderPath) {
    throw new Error('Folder path is required');
  }

  try {
    // Ensure the folderPath has a trailing slash for directories
    const formattedPath = folderPath.endsWith('/') ? folderPath : `${folderPath}/`;

    const token = getToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    // Use the existing createFolder endpoint
    const response = await axios.post(
      UPLOAD_ENDPOINTS.CREATE_FOLDER,
      {
        rootBucket: 'files',
        folderPath: formattedPath
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    // Check response for success
    if (response.status === 200) {
      return true;
    } else {
      console.warn(`Unexpected response creating folder ${formattedPath}:`, response.status);
      return false;
    }
  } catch (error) {
    // Check for specific error indicating folder already exists
    if (error.response && error.response.data &&
      (error.response.status === 409 ||
        error.response.data.message?.includes('already exists'))) {
      return true;
    }

    // For other errors, log but don't throw to allow process to continue
    console.warn(`Error creating folder ${folderPath}:`, error.message);
    // Return true to allow the process to continue, as missing folders will be 
    // created automatically when uploadSingleFile is called with folderParent
    return true;
  }
};


/**
 * Format file size
 * @param {number} bytes - Bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Format date to local time
 * @param {string} dateTime - Date time
 * @returns {string} Formatted date time
 */
export const formatDateTime = (dateTime) => {
  if (!dateTime) return '';

  const date = new Date(dateTime);

  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Get file icon class based on file type
 * @param {Object} item - File or folder item
 * @returns {Object} Icon class and color
 */
export const getFileIconInfo = (item) => {
  if (!item) return { iconClass: 'fa-file', iconColor: '#000000' };

  const isFolder = item.type === 'folder';

  if (isFolder) {
    return { iconClass: 'fa-folder', iconColor: '#FFD700' };
  }

  const extension = item.name.split('.').pop().toLowerCase();

  switch (extension) {
    case 'pdf':
      return { iconClass: 'fa-file-pdf', iconColor: '#FF0000' };
    case 'doc':
    case 'docx':
      return { iconClass: 'fa-file-word', iconColor: '#295496' };
    case 'xls':
    case 'xlsx':
      return { iconClass: 'fa-file-excel', iconColor: '#217346' };
    case 'ppt':
    case 'pptx':
      return { iconClass: 'fa-file-powerpoint', iconColor: '#D24726' };
    case 'txt':
      return { iconClass: 'fa-file-alt', iconColor: '#212529' };
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'svg':
      return { iconClass: 'fa-file-image', iconColor: '#6610f2' };
    case 'mp3':
    case 'wav':
    case 'ogg':
    case 'flac':
      return { iconClass: 'fa-file-audio', iconColor: '#17a2b8' };
    case 'mp4':
    case 'avi':
    case 'mov':
    case 'wmv':
    case 'mkv':
      return { iconClass: 'fa-file-video', iconColor: '#fd7e14' };
    case 'html':
    case 'css':
    case 'js':
      return { iconClass: 'fa-file-code', iconColor: '#e83e8c' };
    case 'zip':
    case 'rar':
    case '7z':
    case 'tar':
    case 'gz':
      return { iconClass: 'fa-file-archive', iconColor: '#ffc107' };
    default:
      return { iconClass: 'fa-file', iconColor: '#6c757d' };
  }
};

// Helper functions for caching
const getCachedData = async (key) => {
  try {
    const cacheItem = sessionStorage.getItem(`cache-${key}`);
    if (!cacheItem) return null;

    const { data, expires } = JSON.parse(cacheItem);
    if (Date.now() > expires) {
      sessionStorage.removeItem(`cache-${key}`);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error getting cache:', error);
    return null;
  }
};

const setCachedData = async (key, data, expirationMinutes = 30) => {
  try {
    const cacheItem = {
      data,
      expires: Date.now() + (expirationMinutes * 60 * 1000)
    };
    sessionStorage.setItem(`cache-${key}`, JSON.stringify(cacheItem));
    return true;
  } catch (error) {
    console.error('Error setting cache:', error);
    return false;
  }
};

/**
 * Rename a file
 * @param {string} oldKey - Current file key/path
 * @param {string} newKey - New file name (just the filename, not full path)
 * @param {string} folderParent - Parent folder path
 * @param {string} bucket - Bucket name
 * @returns {Promise<Object>} Result
 */
export const renameFile = async (oldKey, newKey, folderParent = '', bucket = 'files') => {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  try {

    const response = await fetch(UPLOAD_ENDPOINTS.RENAME_CARD, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        bucket,
        oldKey,
        newKey,
        folderParent
      }),
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(`HTTP error! Status: ${response.status}, Message: ${errorData.message || 'Unknown error'}`);
    }

    const result = await response.json();

    // Clear cache after successful rename
    await clearBucketContentCache();

    return result;
  } catch (error) {
    console.error(`Error renaming file:`, error);
    throw error;
  }
};

const clearBucketContentCache = async () => {
  try {
    // Clear sessionStorage cache
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('cache-')) {
        sessionStorage.removeItem(key);
      }
    });

    return true;
  } catch (error) {
    console.error('Error clearing cache:', error);
    return false;
  }
};